import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { getNextContestNumber } from "@/lib/check-scope";
import { POOL_LIMITS, ticketPrice, generateComplete, combinations, type LotterySlug } from "@/lib/pool-math";
import { generateSmart, computeSumRange, DEFAULT_FILTERS, type NumberStat } from "@/lib/smart-generator";
import { LOTTERIES } from "@/hooks/use-lottery-selection";
import { cn, formatBRL, formatInt } from "@/lib/utils";
import {
  PoolGameBasket, usePoolBasket, gameKey, type GameSource,
} from "@/components/app/boloes/PoolGameBasket";

interface Props {
  poolId: string;
  lotteryId: string;
  lotterySlug: LotterySlug;
  existingGames: number[][];
  contestNumber: number | null;
}

/** Permite acrescentar jogos (montados à mão, combinados ou gerados) a um bolão já criado. */
export function AddGamesDialog({ poolId, lotteryId, lotterySlug, existingGames, contestNumber }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [targetContest, setTargetContest] = useState<number | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [picks, setPicks] = useState<number[]>([]);
  const [k, setK] = useState<number>(POOL_LIMITS[lotterySlug].baseNumbers);
  const [smartQty, setSmartQty] = useState(5);
  const basket = usePoolBasket(lotterySlug, `lt-bolao-add-${poolId}`);

  const config = LOTTERIES.find((l) => l.slug === lotterySlug)!;
  const limits = POOL_LIMITS[lotterySlug];
  const universe = useMemo(
    () => Array.from({ length: config.max - config.min + 1 }, (_, i) => config.min + i),
    [config],
  );
  const existingKeys = useMemo(() => new Set(existingGames.map(gameKey)), [existingGames]);
  const { data: nextContest } = useQuery({
    queryKey: ["next-pool-contest", lotterySlug, open],
    enabled: open,
    queryFn: () => getNextContestNumber(lotterySlug),
  });
  const { data: scheduled = [] } = useQuery({
    queryKey: ["pool-contest-options", lotteryId, nextContest],
    enabled: open && nextContest != null,
    queryFn: async () => {
      const { data, error } = await supabase.from("lottery_schedule")
        .select("contest_number, draw_date, special_name").eq("lottery_id", lotteryId)
        .gte("contest_number", nextContest ?? 1).order("contest_number").limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });
  const availableContests = scheduled.some((item) => item.contest_number === nextContest) ? scheduled
    : nextContest != null ? [{ contest_number: nextContest, draw_date: "", special_name: null }, ...scheduled] : scheduled;

  const { data: smartData } = useQuery({
    queryKey: ["pool-smart-data", lotterySlug],
    enabled: open,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [statsRes, drawsRes] = await Promise.all([
        supabase.rpc("get_number_stats", { _lottery_slug: lotterySlug }),
        supabase
          .from("draws")
          .select("id, draw_numbers(number)")
          .eq("lottery_id", lotteryId)
          .order("contest_number", { ascending: false })
          .limit(1000),
      ]);
      const stats: NumberStat[] = (statsRes.data ?? []).map((s: any) => ({
        number: s.number, frequency: Number(s.frequency), delay: s.delay,
      }));
      const pastDraws = (drawsRes.data ?? [])
        .map((d: any) => (d.draw_numbers ?? []).map((dn: any) => dn.number).sort((a: number, b: number) => a - b))
        .filter((arr: number[]) => arr.length > 0);
      return { stats, pastDraws, sumRange: computeSumRange(pastDraws) };
    },
  });

  const toggle = (n: number) => {
    setPicks((prev) => (prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n].sort((a, b) => a - b)));
  };

  const addGames = (games: number[][], source: GameSource) => {
    const fresh = games.filter((g) => !existingKeys.has(gameKey(g)));
    const blocked = games.length - fresh.length;
    const { added, duplicates } = basket.add(fresh, source);
    const dupes = duplicates + blocked;
    if (added > 0) {
      toast.success(`${added} jogo(s) adicionado(s)${dupes > 0 ? ` • ${dupes} duplicado(s) ignorado(s)` : ""}`);
    } else {
      toast.warning("Esses jogos já estão no bolão ou na lista");
    }
  };

  const addManual = () => {
    if (picks.length < limits.baseNumbers) {
      toast.error(`Escolha ao menos ${limits.baseNumbers} dezenas`);
      return;
    }
    if (picks.length > limits.maxPickPerGame) {
      toast.error(`Máximo de ${limits.maxPickPerGame} dezenas por jogo`);
      return;
    }
    addGames([picks], "manual");
  };

  const addCombinations = () => {
    if (picks.length < k) {
      toast.error(`Escolha ao menos ${k} dezenas`);
      return;
    }
    const total = combinations(picks.length, k);
    if (total > 1000 && !window.confirm(`Serão gerados ${formatInt(total)} jogos. Continuar?`)) return;
    addGames(generateComplete(picks, k), "complete");
  };

  const addSmart = () => {
    if (!smartData) {
      toast.error("Carregando dados estatísticos — aguarde alguns segundos");
      return;
    }
    const r = generateSmart({
      min: config.min, max: config.max, pick: k,
      filters: DEFAULT_FILTERS,
      stats: smartData.stats, pastDraws: smartData.pastDraws, sumRange: smartData.sumRange,
      count: Math.max(1, Math.min(100, smartQty)),
    });
    addGames(r.games, "smart");
  };

  const save = async () => {
    if (basket.games.length === 0) return;
    if (basket.games.length > 1000) {
      toast.error(`É possível confirmar até 1.000 jogos por atualização. A lista tem ${basket.games.length}; remova alguns e confirme em etapas.`);
      return;
    }
    if (nextContest == null || contestNumber == null) {
      toast.error("Não foi possível conferir o concurso do bolão");
      return;
    }
    if (contestNumber < nextContest) {
      if (targetContest == null || targetContest < nextContest || !availableContests.some((item) => item.contest_number === targetContest)) {
        toast.error("Escolha um novo concurso para todos os jogos do bolão");
        return;
      }
      setMoveOpen(true);
      return;
    }
    await confirmUpdate(contestNumber);
  };

  const confirmUpdate = async (chosenContest: number) => {
    setMoveOpen(false);
    setSaving(true);
    try {
      const count = basket.games.length;
      const updatePayload = basket.games.map((game) => game.numbers);
      const invalidGame = updatePayload.find((numbers) =>
        numbers.length < limits.baseNumbers || numbers.length > limits.maxPickPerGame
        || numbers.some((n) => !Number.isInteger(n) || n < config.min || n > config.max)
        || new Set(numbers).size !== numbers.length,
      );
      if (invalidGame) throw new Error("Há um jogo inválido na lista. Remova-o e tente novamente.");
      if (new Set(updatePayload.map(gameKey)).size !== updatePayload.length || updatePayload.some((numbers) => existingKeys.has(gameKey(numbers)))) {
        throw new Error("Há um jogo repetido na lista ou no bolão. Remova-o antes de confirmar.");
      }
      const updateSignature = JSON.stringify({ games: updatePayload, contest: chosenContest });
      const requestStorageKey = `lt-pool-update-request-${poolId}`;
      let requestId: string = crypto.randomUUID();
      try {
        const stored = JSON.parse(localStorage.getItem(requestStorageKey) ?? "null") as {
          signature?: string;
          requestId?: string;
        } | null;
        if (stored?.signature === updateSignature && stored.requestId) {
          requestId = stored.requestId;
        } else {
          localStorage.setItem(requestStorageKey, JSON.stringify({ signature: updateSignature, requestId }));
        }
      } catch {
        // A confirmação continua funcionando mesmo quando o armazenamento local está indisponível.
      }
      const { data, error } = await supabase.functions.invoke("confirm-pool-update", {
        body: {
          poolId,
          requestId,
          games: updatePayload,
          targetContest: chosenContest,
        },
      });
      if (error) {
        const response = "context" in error ? error.context as Response | undefined : undefined;
        const details = response ? await response.clone().json().catch(() => null) : null;
        throw new Error(typeof details?.error === "string" ? details.error : "Não foi possível confirmar a atualização. Tente novamente.");
      }
      if (data?.error) throw new Error(data.error);

      const emailsFailed = Number(data?.emailsFailed ?? 0);
      if (emailsFailed > 0) {
        toast.warning(`Bolão atualizado, mas ${emailsFailed} aviso(s) não puderam ser enviado(s)`);
      } else {
        const emailsSent = Number(data?.emailsSent ?? 0);
        toast.success(
          emailsSent > 0
            ? `${count} jogo(s) incluído(s) e cotistas avisados`
            : `${count} jogo(s) incluído(s) no bolão`,
        );
      }
      basket.clear();
      try { localStorage.removeItem(requestStorageKey); } catch { /* ignore */ }
      setPicks([]);
      setOpen(false);
      setTargetContest(null);
      qc.invalidateQueries({ queryKey: ["pool", poolId] });
      qc.invalidateQueries({ queryKey: ["pool-games", poolId] });
      qc.invalidateQueries({ queryKey: ["pools"] });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro desconhecido";
      toast.error(`Erro ao adicionar jogos: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) setMoveOpen(false); }}>
      <DialogTrigger asChild>
        <Button size="sm"><Plus className="mr-2 h-4 w-4" /> Adicionar jogos</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Adicionar jogos ao bolão</DialogTitle>
          <DialogDescription>
            Monte seu próprio jogo, gere combinações ou use o gerador inteligente.
            Os jogos entram na lista abaixo e só serão incluídos no bolão quando você confirmar a atualização.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <p>Concurso dos jogos: <strong>#{contestNumber ?? "—"}</strong></p>
          {nextContest != null && contestNumber != null && contestNumber < nextContest && (
            <div className="space-y-2">
              <p className="text-destructive">O concurso #{contestNumber} já foi sorteado. Para incluir jogos, é necessário transferir todos os jogos deste bolão para um novo concurso.</p>
              <Select value={targetContest != null ? String(targetContest) : undefined} onValueChange={(value) => setTargetContest(Number(value))}>
                <SelectTrigger><SelectValue placeholder="Escolha o novo concurso" /></SelectTrigger>
                <SelectContent>{availableContests.map((item) => <SelectItem key={item.contest_number} value={String(item.contest_number)}>
                  Concurso #{item.contest_number}{item.special_name ? ` — ${item.special_name}` : ""}
                </SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Tabs defaultValue="manual" className="space-y-4">
          <TabsList>
            <TabsTrigger value="manual">Montar meu jogo</TabsTrigger>
            <TabsTrigger value="complete">Combinações</TabsTrigger>
            <TabsTrigger value="smart"><Wand2 className="mr-1 h-3.5 w-3.5" /> Inteligente</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="space-y-3">
            <Grid universe={universe} picks={picks} toggle={toggle} />
            <div className="flex items-center justify-between gap-2 text-[13px] text-muted-foreground">
              <span>
                {picks.length} dezena(s) • {formatBRL(ticketPrice(lotterySlug, Math.max(limits.baseNumbers, picks.length)))}
              </span>
              <Button size="sm" onClick={addManual}><Plus className="mr-2 h-4 w-4" /> Adicionar à lista</Button>
            </div>
          </TabsContent>

          <TabsContent value="complete" className="space-y-3">
            <Grid universe={universe} picks={picks} toggle={toggle} />
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Dezenas por jogo</Label>
                <Select value={String(k)} onValueChange={(v) => setK(Number(v))}>
                  <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: limits.maxPickPerGame - limits.baseNumbers + 1 }, (_, i) => limits.baseNumbers + i).map((v) => (
                      <SelectItem key={v} value={String(v)}>
                        {v} dezenas — {formatBRL(ticketPrice(lotterySlug, v))}/jogo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" onClick={addCombinations}><Plus className="mr-2 h-4 w-4" /> Gerar e adicionar</Button>
            </div>
          </TabsContent>

          <TabsContent value="smart" className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Dezenas por jogo</Label>
                <Select value={String(k)} onValueChange={(v) => setK(Number(v))}>
                  <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: limits.maxPickPerGame - limits.baseNumbers + 1 }, (_, i) => limits.baseNumbers + i).map((v) => (
                      <SelectItem key={v} value={String(v)}>
                        {v} dezenas — {formatBRL(ticketPrice(lotterySlug, v))}/jogo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Quantidade</Label>
                <Input
                  type="number" min={1} max={100} className="w-28"
                  value={smartQty}
                  onChange={(e) => setSmartQty(Math.max(1, Math.min(100, Number(e.target.value))))}
                />
              </div>
              <Button size="sm" onClick={addSmart} disabled={!smartData}>
                {!smartData && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Wand2 className="mr-2 h-4 w-4" /> Gerar e adicionar
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <PoolGameBasket
          slug={lotterySlug}
          games={basket.games}
          onRemove={basket.removeAt}
          onClear={basket.clear}
          title="Novos jogos"
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving || basket.games.length === 0 || nextContest == null || (contestNumber != null && contestNumber < nextContest && targetContest == null)}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Atualizar Bolão
          </Button>
        </DialogFooter>
      </DialogContent>
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atualizar o concurso de todos os jogos?</DialogTitle>
            <DialogDescription>O concurso #{contestNumber} já foi sorteado. Todos os jogos existentes e os novos passarão a valer para o concurso #{targetContest}. Se cancelar, nenhum jogo será incluído e nenhum aviso será enviado.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)}>Não, cancelar</Button>
            <Button onClick={() => { if (targetContest != null) void confirmUpdate(targetContest); }} disabled={saving}>Sim, atualizar bolão</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function Grid({ universe, picks, toggle }: { universe: number[]; picks: number[]; toggle: (n: number) => void }) {
  return (
    <div className="grid grid-cols-10 gap-1.5 sm:grid-cols-12 md:grid-cols-15">
      {universe.map((n) => {
        const active = picks.includes(n);
        return (
          <button
            key={n}
            onClick={() => toggle(n)}
            className={cn(
              "flex h-9 items-center justify-center rounded-md border text-[12px] font-medium transition-all",
              active
                ? "border-primary bg-primary text-primary-foreground shadow"
                : "border-lt-border bg-lt-bg2 hover:border-primary/50 hover:bg-primary/5",
            )}
          >
            {String(n).padStart(2, "0")}
          </button>
        );
      })}
    </div>
  );
}
